import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { participantApiToParticipant, participantStatusToParticipantApiStatus } from '../../entities/Participant/converters';
import { BaseClient } from '../BaseClient';
export class ParticipantsClientBase extends BaseClient {
  constructor() {
    super(...arguments);
    _defineProperty(this, "getDeleteParticipantImpl", void 0);
    _defineProperty(this, "getParticipantImpl", void 0);
    _defineProperty(this, "queryParticipantsImpl", void 0);
    _defineProperty(this, "updateParticipantImpl", void 0);
    _defineProperty(this, "triggerWatchdogImpl", void 0);
    _defineProperty(this, "triggerParticipantEventImpl", void 0);
  }
  async getDeletedParticipant(participantId) {
    try {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }
      const {
        participant
      } = await this.request(this.getDeleteParticipantImpl({
        participantId
      }), ...args);
      if (!participant) {
        return null;
      }
      return this.convertToEntity(participant);
    } catch (error) {
      this.logError('getDeletedParticipant', 'Failed to get deleted participant', {
        participantId
      }, error);
      throw error;
    }
  }
  async getParticipantByConversationId(conversationId) {
    try {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      const participants = await this.queryParticipants({
        filter: {
          conversationId
        }
      }, ...args);
      if (participants.length === 0) {
        return null;
      }
      if (participants.length === 1) {
        return participants[0];
      }
      this.logDebug('getParticipantByConversationId', 'Multiple participants found, returning latest', {
        participantIds: participants.map(p => p.id)
      });
      return participants.sort((a, b) => {
        const aDate = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const bDate = b.createdDate ? new Date(b.createdDate).getTime() : 0;
        return bDate - aDate;
      })[0];
    } catch (error) {
      this.logError('getParticipantByConversationId', 'Failed to get participant by conversation ID', {
        conversationId
      }, error);
      throw error;
    }
  }
  async getAllParticipantsByConversationId(conversationId) {
    try {
      for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
        args[_key3 - 1] = arguments[_key3];
      }
      const participants = await this.queryParticipants({
        filter: {
          conversationId
        }
      }, ...args);
      this.logDebug('getParticipantByConversationId', 'Found participants', {
        conversationId,
        participantCount: participants.length
      });
      return participants;
    } catch (error) {
      this.logError('getAllParticipantsByConversationId', 'Failed to get all participants by conversation ID', {
        conversationId
      }, error);
      throw error;
    }
  }
  async getParticipant(participantId) {
    try {
      for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        args[_key4 - 1] = arguments[_key4];
      }
      const {
        participant
      } = await this.request(this.getParticipantImpl({
        participantId
      }), ...args);
      return this.convertToEntity(participant);
    } catch (error) {
      this.logError('getParticipant', 'Failed to get participant', {
        participantId
      }, error);
      throw error;
    }
  }
  async updateParticipantConversationId(participantId, revision, conversationId) {
    try {
      for (var _len5 = arguments.length, args = new Array(_len5 > 3 ? _len5 - 3 : 0), _key5 = 3; _key5 < _len5; _key5++) {
        args[_key5 - 3] = arguments[_key5];
      }
      const {
        participant
      } = await this.request(this.updateParticipantImpl({
        participant: {
          id: participantId,
          conversationId,
          revision
        },
        fieldMask: ['conversationId']
      }), ...args);
      return this.convertToEntity(participant);
    } catch (error) {
      this.logError('updateParticipantConversationId', 'Failed to update participant conversation ID', {
        participantId,
        revision,
        conversationId
      }, error);
      throw error;
    }
  }
  async queryParticipants(query) {
    const {
      filter,
      cursorPaging
    } = query;
    try {
      for (var _len6 = arguments.length, args = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
        args[_key6 - 1] = arguments[_key6];
      }
      const {
        participants
      } = await this.request(this.queryParticipantsImpl({
        query: {
          ...(filter && {
            filter
          }),
          ...(cursorPaging && {
            cursorPaging
          })
        }
      }), ...args);
      if (!participants) {
        throw new Error('participants is not an array');
      }
      return participants.map(this.convertToEntity);
    } catch (error) {
      this.logError('queryParticipants', 'Failed to query participants', {
        query
      }, error);
      throw error;
    }
  }
  async updateParticipantStatus(participantId, revision, _ref) {
    let {
      status,
      notes
    } = _ref;
    try {
      for (var _len7 = arguments.length, args = new Array(_len7 > 3 ? _len7 - 3 : 0), _key7 = 3; _key7 < _len7; _key7++) {
        args[_key7 - 3] = arguments[_key7];
      }
      const {
        participant
      } = await this.request(this.updateParticipantImpl({
        participant: {
          id: participantId,
          revision,
          detailedStatus: {
            status: participantStatusToParticipantApiStatus(status),
            notes
          }
        },
        fieldMask: ['detailedStatus']
      }), ...args);
      return this.convertToEntity(participant);
    } catch (error) {
      this.logError('updateParticipantStatus', 'Failed to update participant status', {
        participantId,
        revision,
        status,
        notes
      }, error);
      throw error;
    }
  }
  async updateParticipantContact(participantId, revision, contactId) {
    try {
      for (var _len8 = arguments.length, args = new Array(_len8 > 3 ? _len8 - 3 : 0), _key8 = 3; _key8 < _len8; _key8++) {
        args[_key8 - 3] = arguments[_key8];
      }
      const {
        participant
      } = await this.request(this.updateParticipantImpl({
        participant: {
          id: participantId,
          revision,
          contactId
        },
        fieldMask: ['contactId']
      }), ...args);
      return this.convertToEntity(participant);
    } catch (error) {
      this.logError('updateParticipantContact', 'Failed to update participant contact', {
        participantId,
        revision,
        contactId
      }, error);
      throw error;
    }
  }
  async triggerWatchdog(_ref2) {
    let {
      participantId,
      watchdogWakeupReason,
      wakeup,
      iteration
    } = _ref2;
    try {
      for (var _len9 = arguments.length, args = new Array(_len9 > 1 ? _len9 - 1 : 0), _key9 = 1; _key9 < _len9; _key9++) {
        args[_key9 - 1] = arguments[_key9];
      }
      await this.request(this.triggerWatchdogImpl({
        participantId,
        watchdogWakeupReason,
        wakeup,
        iteration
      }), ...args);
    } catch (error) {
      this.logError('triggerWatchdog', 'Failed to trigger watchdog', {
        participantId,
        watchdogWakeupReason,
        wakeup,
        iteration
      }, error);
      throw error;
    }
  }
  async triggerParticipantEvent(participantId) {
    try {
      for (var _len0 = arguments.length, args = new Array(_len0 > 1 ? _len0 - 1 : 0), _key0 = 1; _key0 < _len0; _key0++) {
        args[_key0 - 1] = arguments[_key0];
      }
      await this.request(this.triggerParticipantEventImpl({
        participantId
      }), ...args);
    } catch (error) {
      this.logError('triggerParticipantEvent', 'Failed to trigger participant event', {
        participantId
      }, error);
      throw error;
    }
  }
  convertToEntity(participant) {
    try {
      return participantApiToParticipant(participant);
    } catch (error) {
      this.logError('convertToEntity', 'Failed to convert participant', {
        participant
      }, error);
      throw error;
    }
  }
  getServiceNameForLogs() {
    return 'ParticipantsClient';
  }
}
//# sourceMappingURL=ParticipantsClientBase.js.map